import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import OwTableCard from "../../shared/OwTableCard/OwTableCard";
import UIService from "../../../services/UIService";
import IWarehouse from "../../../models/IWarehouse";
import IOwTableCols from "../../../models/IOwTableCols";
import IWarehouse_Transfer from "../../../models/IWarehouse_Transfer";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwPill from "../../shared/OwPill/OwPill";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import { WarehouseTransferStatus } from "../../../models/WarehouseTransferStatusEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetWarehouseTransfersDTO from "../../../models/DTO/IGetWarehouseTransfersDTO";
import IOnGetWarehouseTransfersDTO from "../../../models/DTO/IOnGetWarehouseTransfersDTO";
import IOnGetWarehousesDTO from "../../../models/DTO/IOnGetWarehousesDTO";
import IGetWarehousesDTO from "../../../models/DTO/IGetWarehousesDTO";
import { TransfersSidebar } from "../TransfersSidebar/TransfersSidebar";
import { useNavigate } from "react-router-dom";
import IProcessWarehouseTransferDTO from "../../../models/DTO/IProcessWarehouseTransferDTO";
import IOnProcessWarehouseTransferDTO from "../../../models/DTO/IOnProcessWarehouseTransferDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import UserService from "../../../services/UserService";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import "./Transfers.scss";

type Props = {};

// Display config for the transfer status badge (translation key + icon, colors in Transfers.scss)
const TRANSFER_STATUS_PILL: Record<WarehouseTransferStatus, { label: string; icon: string }> = {
    draft: { label: "draft", icon: "fas fa-screwdriver-wrench" },
    in_transit: { label: "in_transit", icon: "fas fa-truck" },
    completed: { label: "completed", icon: "fas fa-check" },
};

export const Transfers = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const history = useNavigate();
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const getBreadcrumbs = () => {
        return [
            {
                title: "inventory",
                url: "/inventory",
            },
            {
                title: "transfers",
                url: "/inventory/transfers",
            },
        ];
    };

    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [pagination, setPagination] = useState({ offset: 0, limit: 100 });
    const [transfers, setTransfers] = useState<IWarehouse_Transfer[]>([]);
    const [totalTransfers, setTotalTransfers] = useState(0);
    const [warehouses, setWarehouses] = useState<IWarehouse[]>([]);
    const [crumbs, setCrumbs] = useState<IBreadcrumbs[]>(getBreadcrumbs());
    const [hideReceivedBy, setHideReceivedBy] = useState(true);
    // Van transfers = driver load/unload bookings. They are created already
    // received, so the filter forces hide-received OFF while active.
    const [vehicleOnly, setVehicleOnly] = useState(false);

    const processWarehouseTransfer = () => {};

    const onProcessWarehouseTransfer = () => {
        const notification: INotification = {
            message: "successfully_processed_warehouse_transfer",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        // Registered once on mount, so call through the ref to pick up the current
        // pagination/filter state rather than the mount-time closure's initial values.
        getTransfersRef.current();
    };

    // offset is the page index (the server multiplies it by limit), as the PaginationBar emits it.
    // Callers pass explicit values when they have just changed state, since setState is async.
    const getTransfers = (
        hideReceived: boolean = hideReceivedBy,
        vansOnly: boolean = vehicleOnly,
        offset: number = pagination.offset,
        limit: number = pagination.limit
    ) => {
        const payload: IGetWarehouseTransfersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            limit,
            offset,
        };

        if (vansOnly) {
            payload.vehicleOnly = true;
        } else if (hideReceived) {
            payload.hideReceived = hideReceived;
        }

        socket.emit(EndpointsEnum.WarehouseGetTransfers, payload);

        setIsDataFetching(true);
    };

    // Latest getTransfers for the mount-registered socket handlers (see onProcessWarehouseTransfer).
    // Synced in an effect (not during render) so the ref only ever points at a committed
    // closure and never exposes pagination/filter state from a render that was discarded.
    const getTransfersRef = useRef(getTransfers);
    useEffect(() => {
        getTransfersRef.current = getTransfers;
    });

    const onGetTransfers = (data: IOnGetWarehouseTransfersDTO) => {
        setTransfers(data.warehouseTransfers);
        setTotalTransfers(data.total);

        setIsDataFetching(false);
        setIsInitialData(false);
    };

    const getWarehouses = () => {
        const data: IGetWarehousesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.WarehouseGetWarehouses, data);
    };

    const onGetWarehouses = (data: IOnGetWarehousesDTO) => {
        setWarehouses(data.warehouses);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.WarehouseOnProcessWarehouseTransfer, fn: onProcessWarehouseTransfer },
        { endpoint: EndpointsEnum.WarehouseOnGetTransfers, fn: onGetTransfers },
        { endpoint: EndpointsEnum.WarehouseOnGetWarehouses, fn: onGetWarehouses },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getWarehouses();
        getTransfers();

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const viewTransfer = (row: IWarehouse_Transfer) => {
        history(`/inventory/transfers/${row.guid}`);
    };

    const renderActionsColFn = (row: IWarehouse_Transfer) => {
        return (
            <div className={"d-flex"}>
                {row.received_at ? null : (
                    <div>
                        <OwIconButton
                            iconClass={"fas fa-trash-alt me-2"}
                            iconColorClass={"text-danger"}
                            fn={() => deleteWarehouseTransfer(row)}
                        />
                        {/*<OwIconButton*/}
                        {/*    iconClass={"fas fa-pencil-alt me-2"}*/}
                        {/*    iconColorClass={"text-primary"}*/}
                        {/*    fn={() => openWarehouseTransferSidebar(row)}*/}
                        {/*/>*/}
                    </div>
                )}
            </div>
        );
    };

    const renderTransferColFn = (row: IWarehouse_Transfer) => {
        return (
            <div className={"transfer-id-cell"}>
                <span className={"transfer-id-link"} onClick={() => viewTransfer(row)}>
                    {row.name}
                </span>
                <div className={"transfer-created"}>
                    <OwLabel title={"created"} noBottomMargin={true} /> {UserService.parseDate(row.created_at as string)}
                </div>
            </div>
        );
    };

    const renderRouteColFn = (row: IWarehouse_Transfer) => {
        // Truck icon marks van (vehicle) warehouses — driver load/unload legs
        // read at a glance (same convention as the Inventory breakdown).
        const warehouseSide = (warehouse?: { name?: string; is_vehicle?: boolean }) => (
            <>
                {warehouse?.is_vehicle ? <i className={"fad fa-truck me-1"} /> : null}
                {warehouse?.name ?? "—"}
            </>
        );

        return (
            <span className={"transfer-route"}>
                {warehouseSide(row.Origin_Warehouse)}
                <i className={"fas fa-arrow-right-long mx-2 opacity-50"} />
                {warehouseSide(row.Destination_Warehouse)}
            </span>
        );
    };

    const renderStatusColFn = (row: IWarehouse_Transfer) => {
        const status: WarehouseTransferStatus = row.received_at ? "completed" : (row.status ?? "draft");
        const config = TRANSFER_STATUS_PILL[status];

        return <OwPill title={config.label} className={"transfer-pill transfer-pill--" + status} leftIconClass={config.icon} />;
    };

    const renderItemsColFn = (row: IWarehouse_Transfer) => {
        // The list endpoint sends item_count; the maps only come down on the detail view.
        const count = row.item_count != null ? Number(row.item_count) : (row.Warehouse_Transfer_Catalog_Items_Maps?.length ?? 0);

        return <span className={"transfer-items"}>{count}</span>;
    };

    const renderActionButtonsColFn = (row: IWarehouse_Transfer) => {
        return (
            <div className={"d-flex justify-content-end align-items-center"}>
                <div className={"me-2"}>
                    <OwButton
                        className={"btn-outline-secondary"}
                        title={"detail"}
                        leftIconClass={"fas fa-eye"}
                        fn={() => {
                            viewTransfer(row);
                        }}
                    />
                </div>
                <div className={"me-2"}>
                    <OwButton
                        className={"btn-outline-secondary"}
                        title={"slip"}
                        leftIconClass={"fas fa-print"}
                        fn={() => {
                            viewTransferSlip(row);
                        }}
                    />
                </div>
                {row.received_at ? (
                    <div className={"text-muted small"}>
                        <OwLabel title={"received_by"} noBottomMargin={true} /> {row.Received_By?.name}{" "}
                        <OwLabel title={"at"} noBottomMargin={true} /> {UserService.parseDate(row.received_at)}
                    </div>
                ) : (
                    <OwButton
                        className={"btn-warning"}
                        title={"receive"}
                        leftIconClass={"fas fa-inbox"}
                        fn={() => {
                            receiveTransfer(row);
                        }}
                    />
                )}
            </div>
        );
    };

    const viewTransferSlip = (row: IWarehouse_Transfer) => {
        history(`/inventory/transfers/${row.guid}/slip`);
    };

    const receiveTransfer = (row: IWarehouse_Transfer) => {
        history(`/inventory/transfers/${row.guid}/received`);
    };

    const columns: IOwTableCols[] = [
        {
            isActionsCol: true,
            title: "",
            valueProp: "",
            renderFn: renderActionsColFn,
            renderFnEmitsRow: true,
        },
        {
            title: "transfer",
            valueProp: "",
            renderFn: renderTransferColFn,
            renderFnEmitsRow: true,
        },
        {
            title: "route",
            valueProp: "",
            renderFn: renderRouteColFn,
            renderFnEmitsRow: true,
        },
        {
            title: "status",
            valueProp: "",
            renderFn: renderStatusColFn,
            renderFnEmitsRow: true,
        },
        {
            title: "items",
            valueProp: "",
            renderFn: renderItemsColFn,
            renderFnEmitsRow: true,
        },
        {
            title: "actions",
            className: "text-end",
            valueProp: "",
            renderFn: renderActionButtonsColFn,
            renderFnEmitsRow: true,
        },
    ];

    const deleteWarehouseTransfer = (transfer: IWarehouse_Transfer) => {
        const payload: IProcessWarehouseTransferDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            warehouseTransferId: transfer.id,
            deleteFlag: true,
        };

        socket.emit(EndpointsEnum.WarehouseProcessWarehouseTransfer, payload);
    };

    const openWarehouseTransferSidebar = (transfer?: IWarehouse_Transfer) => {
        const sideBarComponent = (
            <TransfersSidebar cancelFn={cancelSidebarFn} notifyFn={uiContext.notifyFn} socket={socket} warehouses={warehouses} />
        );

        uiContext.setSidebarComponent(sideBarComponent);
        uiContext.toggleSidebar(true);
    };

    const cancelSidebarFn = (refresh?: boolean) => {
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);

        if (refresh) {
            getTransfers();
        }
    };

    const paginationChanged = (offset: number, limit: number) => {
        setPagination({ offset, limit });
        getTransfers(hideReceivedBy, vehicleOnly, offset, limit);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        switch (name) {
            case "hide_received_by":
                setHideReceivedBy(value);
                getTransfers(value, vehicleOnly);
                break;
            case "vehicle_only":
                setVehicleOnly(value);
                getTransfers(hideReceivedBy, value);
                break;
        }
    };

    return (
        <div className={"Transfers"}>
            <Breadcrumbs crumbs={crumbs} />

            {/* Page title bar — sits on the page, not inside a card */}
            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <div className={"transfers-header-icon me-3"}>
                        <i className={"fad fa-dolly"} />
                    </div>
                    <div>
                        <OwTitle title={"transfers"} defaultMargins={false} className={"mb-0"} />
                        <div className={"transfers-subtitle"}>
                            <OwLabel title={"transfers_description"} noBottomMargin={true} />
                        </div>
                    </div>
                    <div className={"ms-auto"}>
                        <OwButton
                            className={"btn-warning"}
                            title={"create"}
                            leftIconClass={"fas fa-plus"}
                            fn={() => {
                                openWarehouseTransferSidebar();
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Search / filters — its own rounded section */}
            <div className="row mt-3">
                <div className="col-12">
                    <div className={"card " + (UIService.isMainViewLight() ? "" : "dark-card text-white bg-secondary")}>
                        <div className="card-body d-flex align-items-center">
                            {/* Search is not implemented yet (the transfers request has no search term), so keep
                                the input disabled rather than accepting text that does nothing. */}
                            <div className={"transfers-search flex-grow-1"}>
                                <i className={"fas fa-search transfers-search-icon"} />
                                <OwFnInput
                                    name={"transfer_search"}
                                    fn={() => {}}
                                    placeholder={"search_transfers_placeholder"}
                                    className={"transfers-search-input"}
                                    disabled={true}
                                />
                            </div>

                            {!!supplierModel.has_truck_inventory && (
                                <div className={"ms-3"}>
                                    <OwCheckbox
                                        fn={handleChange}
                                        name={"vehicle_only"}
                                        truthyVal={true}
                                        falsyVal={false}
                                        checked={vehicleOnly}
                                        label={"van_transfers"}
                                    />
                                </div>
                            )}
                            <div className={"ms-3"}>
                                <OwCheckbox
                                    fn={handleChange}
                                    name={"hide_received_by"}
                                    truthyVal={true}
                                    falsyVal={false}
                                    checked={hideReceivedBy}
                                    label={"hide_received"}
                                    disabled={vehicleOnly}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Open transfers table card */}
            <div className="row mt-3">
                <div className="col-12">
                    <OwTableCard
                        title={"open_transfers"}
                        count={totalTransfers}
                        columns={columns}
                        dataSet={transfers}
                        dataFetch={isDataFetching}
                        initialData={isInitialData}
                        tableConfig={{ tableStriped: true, hasDetailView: false }}
                        offset={pagination.offset}
                        limit={pagination.limit}
                        totalRecords={totalTransfers}
                        onPaginationChanged={paginationChanged}
                    />
                </div>
            </div>
        </div>
    );
};
